/**
 * useNotifications Hook
 * Manages notification fetching, polling, and mutations using React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useCallback, useEffect } from 'react'
import {
  fetchNotifications,
  fetchUnreadCount,
  dismissNotification,
  dismissAllNotifications,
} from '@/api/notifications'
import type {
  Notification,
  NotificationQueryOptions,
  DismissAllOptions,
} from '@/types/notifications'
import { useAuthStore } from '@/store/authStore'

// Query keys for React Query
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (options: NotificationQueryOptions) =>
    [...notificationKeys.all, 'list', options] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
}

// Default polling interval (2 minutes)
const DEFAULT_POLLING_INTERVAL = 2 * 60 * 1000

interface UseNotificationsOptions {
  /**
   * Number of notifications to fetch
   * @default 20
   */
  limit?: number
  /**
   * Offset for pagination
   * @default 0
   */
  offset?: number
  /**
   * Filter by read status
   * @default undefined (fetch all)
   */
  isRead?: boolean
  /**
   * Enable automatic polling for new notifications
   * @default true
   */
  enablePolling?: boolean
  /**
   * Polling interval in milliseconds
   * @default 20000 (20 seconds)
   */
  pollingInterval?: number
}

interface UseNotificationsReturn {
  // Data
  notifications: Notification[]
  unreadCount: number
  totalCount: number
  hasUnreadNotifications: boolean
  
  // Loading states
  isLoading: boolean
  isFetching: boolean
  isRefetching: boolean
  error: Error | null
  
  // Actions
  markAsRead: (id: number) => Promise<void>
  markAllAsRead: (options?: DismissAllOptions) => Promise<void>
  refetch: () => void
  
  // Polling control
  isPollingEnabled: boolean
  setPollingEnabled: (enabled: boolean) => void
}

export function useNotifications(
  options: UseNotificationsOptions = {}
): UseNotificationsReturn {
  const {
    limit = 20,
    offset = 0,
    isRead,
    enablePolling = true,
    pollingInterval = DEFAULT_POLLING_INTERVAL,
  } = options

  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()
  const [pollingEnabled, setPollingEnabled] = useState(enablePolling)

  // Calculate effective refetch interval
  const refetchInterval = pollingEnabled && isAuthenticated ? pollingInterval : false

  // Main notifications query
  const {
    data,
    isLoading,
    isFetching,
    isRefetching,
    error,
    refetch,
  } = useQuery({
    queryKey: notificationKeys.list({ limit, offset, isRead }),
    queryFn: () => fetchNotifications({ limit, offset, isRead }),
    enabled: isAuthenticated,
    refetchInterval,
    refetchIntervalInBackground: false, // Don't poll when tab is not visible
    staleTime: 60 * 1000, // Consider data stale after 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  })

  // Separate query for unread count (for badge - more frequent updates)
  const { data: unreadData } = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: fetchUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: pollingEnabled && isAuthenticated ? pollingInterval / 2 : false,
    refetchIntervalInBackground: false, // Don't poll when tab is not visible
    staleTime: 30 * 1000,
  })

  // Mark single notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: dismissNotification,
    onMutate: async (notificationId: number) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.all })

      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData(
        notificationKeys.list({ limit, offset, isRead })
      )

      // Optimistically update the notification
      queryClient.setQueryData(
        notificationKeys.list({ limit, offset, isRead }),
        (old: { notifications: Notification[]; totalCount: number } | undefined) => {
          if (!old) return old
          return {
            ...old,
            notifications: old.notifications.map((n) =>
              n.id === notificationId
                ? { ...n, isRead: true, readAt: new Date().toISOString() }
                : n
            ),
          }
        }
      )

      // Optimistically update unread count
      queryClient.setQueryData(
        notificationKeys.unreadCount(),
        (old: number | undefined) => (old ?? 1) - 1
      )

      return { previousNotifications }
    },
    onError: (err, notificationId, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          notificationKeys.list({ limit, offset, isRead }),
          context.previousNotifications
        )
      }
      console.error('Failed to mark notification as read:', err)
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })

  // Mark all notifications as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: dismissAllNotifications,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all })

      const previousNotifications = queryClient.getQueryData(
        notificationKeys.list({ limit, offset, isRead })
      )

      // Optimistically mark all as read
      queryClient.setQueryData(
        notificationKeys.list({ limit, offset, isRead }),
        (old: { notifications: Notification[]; totalCount: number } | undefined) => {
          if (!old) return old
          return {
            ...old,
            notifications: old.notifications.map((n) => ({
              ...n,
              isRead: true,
              readAt: new Date().toISOString(),
            })),
          }
        }
      )

      // Set unread count to 0
      queryClient.setQueryData(notificationKeys.unreadCount(), 0)

      return { previousNotifications }
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          notificationKeys.list({ limit, offset, isRead }),
          context.previousNotifications
        )
      }
      console.error('Failed to mark all notifications as read:', err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })

  // Action handlers
  const markAsRead = useCallback(
    async (id: number) => {
      await markAsReadMutation.mutateAsync(id)
    },
    [markAsReadMutation]
  )

  const markAllAsRead = useCallback(
    async (dismissOptions?: DismissAllOptions) => {
      await markAllAsReadMutation.mutateAsync(dismissOptions)
    },
    [markAllAsReadMutation]
  )

  // Pause polling when window is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setPollingEnabled(false)
      } else if (enablePolling) {
        setPollingEnabled(true)
        // Refetch immediately when tab becomes visible
        refetch()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enablePolling, refetch])

  // Derived values
  const notifications = data?.notifications ?? []
  const totalCount = data?.totalCount ?? 0
  const unreadCount = unreadData ?? notifications.filter((n) => !n.isRead).length
  const hasUnreadNotifications = unreadCount > 0

  return {
    // Data
    notifications,
    unreadCount,
    totalCount,
    hasUnreadNotifications,

    // Loading states
    isLoading,
    isFetching,
    isRefetching,
    error: error as Error | null,

    // Actions
    markAsRead,
    markAllAsRead,
    refetch,

    // Polling control
    isPollingEnabled: pollingEnabled,
    setPollingEnabled,
  }
}

/**
 * Hook for just the unread count (lightweight for header badge)
 */
export function useUnreadNotificationCount(): {
  count: number
  isLoading: boolean
} {
  const { isAuthenticated } = useAuthStore()

  const { data, isLoading } = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: fetchUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? DEFAULT_POLLING_INTERVAL / 2 : false,
    refetchIntervalInBackground: false, // Don't poll when tab is not visible
    staleTime: 30 * 1000,
  })

  return {
    count: data ?? 0,
    isLoading,
  }
}
